import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Search, Plus, Pencil, Trash2, Car } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const camposVacios = { cliente_id: '', marca: '', modelo: '', anio: '', placas: '', color: '', vin: '', kilometraje: '', notas: '' };

export default function Vehiculos() {
  const [vehiculos, setVehiculos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(camposVacios);
  const [guardando, setGuardando] = useState(false);
  const [eliminarId, setEliminarId] = useState(null);
  const navigate = useNavigate();

  const cargar = useCallback(async () => {
    try {
      const [vRes, cRes] = await Promise.all([
        api.get('/vehiculos', { params: { busqueda, cliente_id: filtroCliente === 'all' ? '' : filtroCliente } }),
        api.get('/clientes')
      ]);
      setVehiculos(vRes.data);
      setClientes(cRes.data);
    } catch { toast.error('Error al cargar vehiculos'); }
    finally { setCargando(false); }
  }, [busqueda, filtroCliente]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrir = (vehiculo = null) => {
    setEditando(vehiculo);
    setForm(vehiculo ? {
      cliente_id: vehiculo.cliente_id, marca: vehiculo.marca, modelo: vehiculo.modelo,
      anio: vehiculo.anio, placas: vehiculo.placas, color: vehiculo.color,
      vin: vehiculo.vin, kilometraje: vehiculo.kilometraje, notas: vehiculo.notas || ''
    } : camposVacios);
    setDialog(true);
  };

  const guardar = async () => {
    if (!form.marca || !form.modelo || !form.cliente_id) { toast.error('Marca, modelo y cliente son obligatorios'); return; }
    setGuardando(true);
    try {
      if (editando) { await api.put(`/vehiculos/${editando.id}`, form); toast.success('Vehiculo actualizado'); }
      else { await api.post('/vehiculos', form); toast.success('Vehiculo creado'); }
      setDialog(false);
      cargar();
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const eliminar = async () => {
    try {
      await api.delete(`/vehiculos/${eliminarId}`);
      toast.success('Vehiculo eliminado');
      setEliminarId(null);
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="vehiculos-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50">Vehiculos</h1>
        <Button onClick={() => abrir()} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="nuevo-vehiculo-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Vehiculo
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input placeholder="Buscar vehiculos..." value={busqueda} onChange={e => setBusqueda(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600" data-testid="buscar-vehiculos-input" />
        </div>
        <Select value={filtroCliente} onValueChange={setFiltroCliente}>
          <SelectTrigger className="w-full sm:w-[200px] bg-slate-900 border-slate-800 text-slate-200" data-testid="filtro-cliente-select">
            <SelectValue placeholder="Todos los clientes" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all" className="text-slate-300">Todos los clientes</SelectItem>
            {clientes.map(c => <SelectItem key={c.id} value={c.id} className="text-slate-300">{c.nombre_completo}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {cargando ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 bg-slate-800" />)}</div>
      ) : vehiculos.length === 0 ? (
        <div className="text-center py-16" data-testid="vehiculos-empty">
          <Car className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">No hay vehiculos registrados</p>
        </div>
      ) : (
        <div className="bg-slate-900/40 border border-slate-800 rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Placas</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Vehiculo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden md:table-cell">Cliente</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden lg:table-cell">Color</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden lg:table-cell">Km</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehiculos.map(v => (
                <TableRow key={v.id} className="border-slate-800/50 hover:bg-slate-800/40 cursor-pointer"
                  onClick={() => navigate(`/vehiculos/${v.id}`)} data-testid={`vehiculo-row-${v.id}`}>
                  <TableCell className="font-mono text-xs text-amber-500 font-semibold">{v.placas}</TableCell>
                  <TableCell className="text-sm text-slate-200">{v.marca} {v.modelo} {v.anio}</TableCell>
                  <TableCell className="text-sm text-slate-400 hidden md:table-cell">{v.cliente_nombre}</TableCell>
                  <TableCell className="text-sm text-slate-400 hidden lg:table-cell">{v.color}</TableCell>
                  <TableCell className="text-sm text-slate-400 hidden lg:table-cell font-mono">{v.kilometraje ? `${Number(v.kilometraje).toLocaleString()} km` : '-'}</TableCell>
                  <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => abrir(v)} className="text-slate-400 hover:text-white h-8 w-8" data-testid={`editar-vehiculo-${v.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setEliminarId(v.id)} className="text-slate-400 hover:text-red-400 h-8 w-8" data-testid={`eliminar-vehiculo-${v.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="vehiculo-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">{editando ? 'Editar Vehiculo' : 'Nuevo Vehiculo'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Cliente *</Label>
              <Select value={form.cliente_id} onValueChange={v => setForm({...form, cliente_id: v})}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="vehiculo-cliente-select"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {clientes.map(c => <SelectItem key={c.id} value={c.id} className="text-slate-300">{c.nombre_completo}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Marca *</Label>
                <Input value={form.marca} onChange={e => setForm({...form, marca: e.target.value})} className="bg-slate-950 border-slate-700 text-slate-200" data-testid="vehiculo-marca-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Modelo *</Label>
                <Input value={form.modelo} onChange={e => setForm({...form, modelo: e.target.value})} className="bg-slate-950 border-slate-700 text-slate-200" data-testid="vehiculo-modelo-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Año</Label>
                <Input value={form.anio} onChange={e => setForm({...form, anio: e.target.value})} className="bg-slate-950 border-slate-700 text-slate-200" data-testid="vehiculo-anio-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Placas</Label>
                <Input value={form.placas} onChange={e => setForm({...form, placas: e.target.value})} className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="vehiculo-placas-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Color</Label>
                <Input value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="bg-slate-950 border-slate-700 text-slate-200" data-testid="vehiculo-color-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">VIN</Label>
                <Input value={form.vin} onChange={e => setForm({...form, vin: e.target.value})} className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="vehiculo-vin-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Kilometraje</Label>
                <Input value={form.kilometraje} onChange={e => setForm({...form, kilometraje: e.target.value})} className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="vehiculo-km-input" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Notas</Label>
              <Textarea value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} className="bg-slate-950 border-slate-700 text-slate-200" rows={2} data-testid="vehiculo-notas-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={guardar} disabled={guardando} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="guardar-vehiculo-btn">{guardando ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!eliminarId} onOpenChange={() => setEliminarId(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader><AlertDialogTitle className="text-slate-100">Eliminar vehiculo</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">Esta accion no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 text-slate-300 border-slate-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirmar-eliminar-vehiculo-btn">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
